import { memo, useEffect, useState } from 'react';

import { analytics } from '@/features/analytics/service';
import {
  useCloseModal,
  useRemoveModal,
  useReopenModal,
} from '@/shared/components/protectedModals/atoms/modalStack';
import type { ModalStackItem } from '@/shared/components/protectedModals/atoms/modalStack/types';
import { ResponsiveDialog } from '@/shared/components/ui/custom/responsive-dialog';

import { BenefitsPage } from './benefitsPage';
import type { PaywallSource } from './hooks/useOpenPaywallModal';
import { PickPlanPage } from './pickPlanPage';
import { SubscriptionSuccessPage } from './subscriptionSuccessPage';

export type PaywallInitialPage =
  | 'benefits'
  | 'pick-plan'
  | 'subscription-success';

export interface PaywallModalData {
  source: PaywallSource;
  initialPage?: PaywallInitialPage;
}

export interface PaywallModalProps extends ModalStackItem {
  type: 'paywall-modal';
  data: PaywallModalData;
}

type Page =
  | { type: 'benefits' }
  | { type: 'pick-plan' }
  | { type: 'subscription-success' };

function getInitialPageState(initialPage?: PaywallInitialPage): Page {
  switch (initialPage) {
    case 'pick-plan':
      return { type: 'pick-plan' };
    case 'subscription-success':
      return { type: 'subscription-success' };
    default:
      return { type: 'benefits' };
  }
}

export const PaywallModal = memo(function PaywallModal(
  props: PaywallModalProps,
) {
  const { id, isOpen, data } = props;
  const closeModal = useCloseModal();
  const removeModal = useRemoveModal();
  const reopenModal = useReopenModal();

  const [page, setPage] = useState<Page>(() =>
    getInitialPageState(data.initialPage),
  );

  useEffect(() => {
    analytics.captureEvent('Duos Subscription Modal Opened', {
      source: data.source,
    });
  }, [data.source]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal(id);
    }
  };

  const handleAnimationEnd = (open: boolean) => {
    if (!open) {
      removeModal(id);
    }
  };

  const handleCheckoutOpen = () => {
    // Just close the modal, don't remove it. This keeps the component mounted
    // so that handleCheckoutClose callback works when Paddle checkout completes.
    closeModal(id);
  };

  const handleCheckoutClose = (
    result: 'subscribed' | 'cancelled' | 'error',
  ) => {
    if (result === 'subscribed') {
      setPage({ type: 'subscription-success' });
      reopenModal(id);
    } else if (result === 'cancelled' || result === 'error') {
      // Re-open the modal to show pick-plan page again
      reopenModal(id);
    }
  };

  const renderPage = () => {
    if (page.type === 'benefits') {
      return (
        <BenefitsPage
          onContinue={() => {
            setPage({ type: 'pick-plan' });
          }}
        />
      );
    }

    if (page.type === 'pick-plan') {
      return (
        <PickPlanPage
          onCheckoutOpen={handleCheckoutOpen}
          onCheckoutClose={handleCheckoutClose}
        />
      );
    }

    return (
      <SubscriptionSuccessPage
        onClose={() => {
          closeModal(id);
        }}
      />
    );
  };

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      onAnimationEnd={handleAnimationEnd}
    >
      {renderPage()}
    </ResponsiveDialog>
  );
});
